import { getCombinedStatusConfig } from "../../shared/combinedStatusUtil";
import { PaymentBatchEdges, PaymentBatchRowItem } from "./types";

/**
 * The status value indicating a payment batch is ready for treasury review.
 */
const READY_FOR_TREASURY_REVIEW_STATUS = "READY_FOR_TREASURY_REVIEW";

/**
 * Determines if a Canopy distribution payment batch requires FA review.
 * For Canopy, we always enforce 1 FA review when status is READY_FOR_TREASURY_REVIEW,
 * regardless of the requiredApprovalCount setting (that setting is only checked
 * at the GP review stage in the Treasury Dashboard).
 *
 * @param status - The payment batch status (string or enum value)
 * @returns true if the batch requires review, false otherwise
 */
export const paymentBatchRequiresReview = (status: string): boolean => {
  return status === READY_FOR_TREASURY_REVIEW_STATUS;
};

/**
 * Maps GraphQL payment batch edges to row data for the AG Grid table
 * @param edges - The edges from the GraphQL query result
 * @param isGPX - Whether this is GPX (true) or Canopy (false). Defaults to false for Canopy distributions
 * @returns Array of PaymentBatchRowItem for display in the table
 */
export const mapEdgesToRowData = (
  edges: PaymentBatchEdges | null | undefined,
  isGPX?: boolean,
): PaymentBatchRowItem[] => {
  if (!edges) {
    return [];
  }
  return edges.map((edge) => {
    const batch = edge.node;
    // Compute the action for this batch:
    // - Status must be READY_FOR_TREASURY_REVIEW
    // - Show Review button for ALL users (including creator)
    const isReadyForReview = paymentBatchRequiresReview(batch.status);
    const action = isReadyForReview ? ("review" as const) : null;

    // Get payment method from fundingPayment
    const paymentMethod = batch.fundingPayment?.method;

    // Map payments to the expected format
    const payments = batch.payments.map((payment) => ({
      objectId: payment.objectId,
      status: payment.status,
      method: payment.method,
      amount: payment.amount,
      scheduledProcessDate: payment.scheduledProcessDate,
    }));

    // Calculate combined status config - pass isGPX and payment method separately
    const combinedStatusConfig = getCombinedStatusConfig(
      batch.status,
      payments,
      isGPX,
      paymentMethod,
    );

    return {
      id: batch.id,
      createdDate: batch.createdAt,
      objectsCount: batch.objectsCount,
      netAmount: batch.netAmount,
      status: batch.status,
      paymentMethod,
      payments,
      combinedStatusConfig,
      creatorId: batch.creatorId,
      action,
    };
  });
};
